import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useRef, useState, useCallback } from 'react';

function HomeIcon({ size, active }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={active ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
      <path d="M3 12L12 3l9 9v9a2 2 0 01-2 2h-4v-7h-6v7H5a2 2 0 01-2-2v-9z" />
    </svg>
  );
}
function StarIcon({ size, active }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={active ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}
function BrainIcon({ size, active }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={active ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
      <path d="M9.5 2A2.5 2.5 0 0 1 12 4.5v15a2.5 2.5 0 0 1-4.96.44 2.5 2.5 0 0 1-2.96-3.08 3 3 0 0 1-.34-5.58 2.5 2.5 0 0 1 1.32-4.24 2.5 2.5 0 0 1 4.44-1.04Z" />
      <path d="M14.5 2A2.5 2.5 0 0 0 12 4.5v15a2.5 2.5 0 0 0 4.96.44 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-4.44-1.04Z" />
    </svg>
  );
}
function SettingsIcon({ size, active }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={active ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

// Bouclier — les feedbacks interceptés sont le « pare-feu » de Revyo.
function ShieldIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
         strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

const TABS = [
  { id: 'home', path: '/m/home', label: 'Accueil', icon: HomeIcon },
  { id: 'avis', path: '/m/avis', label: 'Avis', icon: StarIcon },
  { id: 'insights', path: '/m/insights', label: 'Insights', icon: BrainIcon },
  { id: 'feedbacks', path: '/m/feedbacks', label: 'Feedbacks', icon: ShieldIcon },
  { id: 'profil', path: '/m/profil', label: 'Profil', icon: SettingsIcon },
];

export default function MobileLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const activeTab = TABS.find(t => location.pathname.startsWith(t.path))?.id || 'home';

  // Re-tap sur l'onglet actuel : scroll-to-top fluide + dispatch event
  // 'revyo:tab-refresh' que les pages peuvent écouter pour refresh leurs
  // données (pattern iOS natif). Sinon : navigation classique.
  /* ═════════════════════════════════════════════════════════════════════
   * Scroll interne + pull-to-refresh
   * ─────────────────────────────────────────────────────────────────────
   * Auparavant c'était le DOCUMENT qui défilait. En bas de page, le
   * rubber-band d'iOS faisait rebondir tout le document — et dans une
   * WKWebView, un élément en position:fixed suit ce rebond : la tab bar
   * semblait « monter ». `overscroll-behavior-y: contain` (index.css)
   * n'y change rien, il n'empêche que la PROPAGATION au parent, pas le
   * rebond lui-même.
   *
   * Désormais seul ce conteneur défile, le document ne bouge plus : la
   * tab bar, hors de la zone scrollable, reste rigoureusement fixe.
   * Bénéfice second : ce conteneur nous donne l'accroche du geste
   * « tirer pour rafraîchir ».
   * ═══════════════════════════════════════════════════════════════════ */
  const scrollRef = useRef(null);
  const touchStartY = useRef(0);
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const PULL_TRIGGER = 70;   // seuil de déclenchement
  const PULL_MAX = 110;      // au-delà, l'indicateur ne descend plus

  const onTouchStart = useCallback((e) => {
    // Le geste ne s'arme QUE si l'on est déjà tout en haut : sinon on
    // volerait un scroll normal à l'utilisateur.
    if (scrollRef.current && scrollRef.current.scrollTop <= 0) {
      touchStartY.current = e.touches[0].clientY;
    } else {
      touchStartY.current = 0;
    }
  }, []);

  const onTouchMove = useCallback((e) => {
    if (!touchStartY.current || refreshing) return;
    const delta = e.touches[0].clientY - touchStartY.current;
    if (delta > 0 && scrollRef.current?.scrollTop <= 0) {
      // Résistance progressive : le doigt avance plus vite que l'indicateur,
      // ce qui donne la sensation d'élasticité des applications natives.
      setPullDistance(Math.min(delta * 0.5, PULL_MAX));
    }
  }, [refreshing]);

  const onTouchEnd = useCallback(() => {
    if (pullDistance >= PULL_TRIGGER && !refreshing) {
      if ('vibrate' in navigator) navigator.vibrate(10);
      setRefreshing(true);
      // Les pages écoutent déjà cet évènement pour le re-tap d'onglet : on
      // réutilise le même canal plutôt que d'en inventer un second.
      window.dispatchEvent(new CustomEvent('revyo:tab-refresh', { detail: { tab: activeTab } }));
      // Laisse le temps aux requêtes de repartir ; l'indicateur ne doit pas
      // disparaître avant que l'écran ait visiblement bougé.
      setTimeout(() => { setRefreshing(false); setPullDistance(0); }, 900);
    } else {
      setPullDistance(0);
    }
    touchStartY.current = 0;
  }, [pullDistance, refreshing, activeTab]);

  const handleTabClick = (tab) => {
    if ('vibrate' in navigator) navigator.vibrate(10);
    if (location.pathname.startsWith(tab.path)) {
      scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      window.dispatchEvent(
        new CustomEvent('revyo:tab-refresh', { detail: { tab: tab.id } })
      );
    } else {
      navigate(tab.path);
    }
  };

  return (
    <>
      <div
        ref={scrollRef}
        style={styles.container}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {/* Indicateur de rafraîchissement — n'occupe aucune place tant que le
            geste n'a pas commencé. */}
        <div
          style={{
            ...styles.pullIndicator,
            height: refreshing ? 44 : pullDistance,
            opacity: pullDistance > 8 || refreshing ? 1 : 0,
          }}
          aria-hidden={!refreshing}
        >
          <span style={{
            ...styles.pullSpinner,
            transform: refreshing
              ? 'rotate(360deg)'
              : `rotate(${Math.min(pullDistance * 3, 360)}deg)`,
            animation: refreshing ? 'revyo-spin 0.8s linear infinite' : 'none',
          }}>↻</span>
        </div>
        <main style={styles.content}>{children}</main>
      </div>

      {/* nav SORTIE du container : position:fixed est maintenant
          strictement relatif au viewport. Fix Bug Build 8 où la tab bar
          montait avec le scroll dans certaines WebView Capacitor iOS. */}
      <nav style={styles.tabBar}>
        {TABS.map(tab => {
          const isActive = activeTab === tab.id;
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => handleTabClick(tab)}
              style={{
                ...styles.tabBtn,
                color: isActive ? '#E8502A' : '#6B6B6B',
              }}
              aria-label={tab.label}
              aria-current={isActive ? 'page' : undefined}
            >
              <Icon size={24} active={isActive} />
              <span style={styles.tabLabel}>{tab.label}</span>
            </button>
          );
        })}
      </nav>
    </>
  );
}

const styles = {
  pullIndicator: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    transition: 'opacity 0.2s ease',
    color: '#E8502A',
  },
  pullSpinner: { fontSize: 22, lineHeight: 1, display: 'inline-block' },
  container: {
    // 100dvh (dynamic viewport height) au lieu de 100vh : sur iOS avec Dynamic
    // Island élargie (appel actif, timer, etc.), 100vh est figé sur le viewport
    // stable initial → le contenu déborde sous la tab bar. dvh est recalculé
    // dynamiquement par Safari WebView 15.4+ (dispo sur tous les iPhones avec DI).
    // hauteur FIXE + overflow interne : c'est ce conteneur qui défile, plus le
    // document. Sans cela, le rebond iOS entraînait la tab bar avec lui.
    height: '100dvh',
    overflowY: 'auto',
    overflowX: 'hidden',
    WebkitOverflowScrolling: 'touch',
    // `contain` ici a un sens : il empêche le scroll de remonter au document,
    // lequel ne défile plus du tout.
    overscrollBehaviorY: 'contain',
    background: '#EDEAE3',
    paddingTop: 'env(safe-area-inset-top)',
    // overscroll-behavior géré maintenant via html.is-native dans index.css
    // (commit native-only feedback). Le mettre ici créait un scroll-containment
    // qui cassait position:fixed de la tab bar sur iOS WebView (Bug Build 7).
    // Le paddingBottom est géré par `content` (cf. ci-dessous) — sinon les pages
    // enfants en minHeight créaient un conflit de double-réserve qui faisait que
    // le dernier élément se retrouvait masqué par la tab bar.
  },
  content: {
    // Réserve la hauteur de la tab bar (49px iOS standard) + safe-area home indicator
    // + 16px de marge confort. Ainsi le dernier élément scrollable a toujours
    // un buffer visible au-dessus de la tab bar.
    paddingBottom: 'calc(49px + env(safe-area-inset-bottom) + 16px)',
  },
  tabBar: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    height: 'calc(49px + env(safe-area-inset-bottom))',
    paddingBottom: 'env(safe-area-inset-bottom)',
    background: 'rgba(255,255,255,0.92)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    borderTop: '1px solid rgba(0,0,0,0.08)',
    display: 'flex',
    justifyContent: 'space-around',
    alignItems: 'center',
    zIndex: 1000,
    // Force GPU compositing layer iOS — defense in depth contre les bugs
    // position:fixed qui peuvent survenir dans certaines WebView Capacitor.
    transform: 'translateZ(0)',
    WebkitTransform: 'translateZ(0)',
  },
  tabBtn: {
    flex: 1,
    height: 49,
    background: 'none',
    border: 'none',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    cursor: 'pointer',
    transition: 'transform 0.15s ease',
    fontFamily: 'inherit',
  },
  tabLabel: {
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: 0.1,
  },
};
